import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPluginRegistration } from "@embedpdf/core";
import { EmbedPDF } from "@embedpdf/core/react";
import { usePdfiumEngine } from "@embedpdf/engines/react";
import {
  Viewport,
  ViewportPluginPackage,
} from "@embedpdf/plugin-viewport/react";
import {
  Scroller,
  ScrollPluginPackage,
  useScroll,
} from "@embedpdf/plugin-scroll/react";
import { LoaderPluginPackage } from "@embedpdf/plugin-loader/react";
import {
  RenderLayer,
  RenderPluginPackage,
} from "@embedpdf/plugin-render/react";
import {
  ZoomPluginPackage,
  ZoomMode,
  useZoom,
} from "@embedpdf/plugin-zoom/react";
import { ThumbnailPluginPackage } from "@embedpdf/plugin-thumbnail/react";
import { PrintPluginPackage } from "@embedpdf/plugin-print/react";
import { PanPluginPackage } from "@embedpdf/plugin-pan/react";
import { RotatePluginPackage, useRotate } from "@embedpdf/plugin-rotate/react";
import { SpreadPluginPackage } from "@embedpdf/plugin-spread/react";
import {
  FullscreenPluginPackage,
  useFullscreen,
} from "@embedpdf/plugin-fullscreen/react";
import {
  ExportPluginPackage,
  useExportCapability,
} from "@embedpdf/plugin-export/react";
import { SearchPluginPackage } from "@embedpdf/plugin-search/react";
import { SelectionPluginPackage } from "@embedpdf/plugin-selection/react";
import { InteractionManagerPluginPackage } from "@embedpdf/plugin-interaction-manager/react";
import { TilingPluginPackage } from "@embedpdf/plugin-tiling/react";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Toolbar,
  TextField,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import "./pdfViewer.css";
import { useDispatch, useSelector } from "react-redux";
import {
  viewPolicyDocument,
  viewQuoteDocument,
} from "../../../../actions/insurance";

// Custom Toolbar Component
const PdfToolbar = ({ onDownloadClick }) => {
  const { provides: scroll, state: scrollState } = useScroll();
  const { provides: zoom, state: zoomState } = useZoom();
  const { provides: exportProvider } = useExportCapability();
  const { provides: fullscreenProvider, state: fullscreenState } =
    useFullscreen();
  const { rotation, provides: rotateProvider } = useRotate();

  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pageInput, setPageInput] = useState("1");
  const [moreActionsAnchor, setMoreActionsAnchor] = useState(null);
  const [darkTheme, setDarkTheme] = useState(true);
  const fullscreenRef = useRef(null);

  // Update page input when current page changes
  useEffect(() => {
    setPageInput(String(scrollState.currentPage || 1));
  }, [scrollState.currentPage]);

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const pageNum = parseInt(pageInput, 10);
    if (pageNum >= 1 && pageNum <= (scrollState.totalPages || 1)) {
      scroll?.scrollToPage({ pageNumber: pageNum });
    }
  };

  const handleZoomIn = () => {
    zoom?.zoomIn();
  };

  const handleZoomOut = () => {
    zoom?.zoomOut();
  };

  const handlePreviousPage = () => {
    scroll?.scrollToPreviousPage();
  };

  const handleNextPage = () => {
    scroll?.scrollToNextPage();
  };

  const handleDownload = () => {
    exportProvider?.download();
    onDownloadClick();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleFullscreen = () => {
    fullscreenProvider?.toggleFullscreen();
  };

  const handleRotateClockwise = () => {
    rotateProvider?.rotateForward();
    setMoreActionsAnchor(null);
  };

  const handleRotateCounterClockwise = () => {
    rotateProvider?.rotateBackward();
    setMoreActionsAnchor(null);
  };

  const handleFirstPage = () => {
    scroll?.scrollToPage({ pageNumber: 1 });
    setMoreActionsAnchor(null);
  };

  const handleLastPage = () => {
    scroll?.scrollToPage({ pageNumber: scrollState.totalPages || 1 });
    setMoreActionsAnchor(null);
  };

  const toggleTheme = () => {
    setDarkTheme(!darkTheme);
    setMoreActionsAnchor(null);
  };

  const handleDocProperties = () => {
    alert(
      `Document Properties:\nTotal Pages: ${
        scrollState.totalPages || "N/A"
      }\nCurrent Rotation: ${rotation || 0}°`
    );
    setMoreActionsAnchor(null);
  };

  const zoomPercentage = Math.round((zoomState.currentZoomLevel || 1) * 100);

  return (
    <>
      <Toolbar
        sx={{
          backgroundColor: "#333",
          color: "white",
          minHeight: "48px !important",
          gap: 1,
          px: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        ref={fullscreenRef}
      >
        {/* Search Toggle */}

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Search">
            <IconButton
              onClick={() => setShowSearch(!showSearch)}
              sx={{ color: "white !important" }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          {showSearch && (
            <TextField
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
              size="small"
              sx={{
                backgroundColor: "white",
                borderRadius: 1,
                width: 250,
                maxWidth: 300,
              }}
              // InputProps={{
              //   endAdornment: (
              //     <IconButton size="small" onClick={() => setShowSearch(false)}>
              //       <CloseIcon fontSize="small" />
              //     </IconButton>
              //   ),
              // }}
            />
          )}

          <Divider
            orientation="vertical"
            flexItem
            sx={{ backgroundColor: "rgba(255,255,255,0.3)", mx: 0.5 }}
          />

          {/* Page Navigation */}
          <Tooltip title="Previous Page">
            <span>
              <IconButton
                onClick={handlePreviousPage}
                disabled={scrollState.currentPage <= 1}
                sx={{ color: "white !important" }}
              >
                <NavigateBeforeIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Box
            component="form"
            onSubmit={handlePageInputSubmit}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <TextField
              value={pageInput}
              onChange={handlePageInputChange}
              size="small"
              sx={{
                backgroundColor: "white",
                width: 50,
                border: "none",
              }}
              inputProps={{ style: { textAlign: "center", padding: "4px" } }}
            />
            <Typography variant="body2" sx={{ color: "white !important" }}>
              / {scrollState.totalPages || 1}
            </Typography>
          </Box>

          <Tooltip title="Next Page">
            <span>
              <IconButton
                onClick={handleNextPage}
                disabled={
                  scrollState.currentPage >= (scrollState.totalPages || 1)
                }
                sx={{ color: "white !important" }}
              >
                <NavigateNextIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ backgroundColor: "rgba(255,255,255,0.3)", mx: 0.5 }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Zoom Controls */}
          <Tooltip title="Zoom Out">
            <IconButton
              onClick={handleZoomOut}
              sx={{ color: "white !important" }}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>

          <Typography
            variant="body2"
            sx={{
              minWidth: 50,
              textAlign: "center",
              color: "white !important",
            }}
          >
            {zoomPercentage}%
          </Typography>

          <Tooltip title="Zoom In">
            <IconButton
              onClick={handleZoomIn}
              sx={{ color: "white !important" }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ backgroundColor: "rgba(255,255,255,0.3)", mx: 0.5 }}
          />

          {/* Download */}
          <Tooltip title="Download">
            <IconButton
              onClick={handleDownload}
              sx={{ color: "white !important" }}
            >
              <GetAppIcon />
            </IconButton>
          </Tooltip>

          {/* Print */}
          <Tooltip title="Print">
            <IconButton
              onClick={handlePrint}
              sx={{ color: "white !important" }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>

          {/* Fullscreen */}
          <Tooltip
            title={
              fullscreenState.isFullscreen ? "Exit Fullscreen" : "Fullscreen"
            }
          >
            <IconButton
              onClick={handleFullscreen}
              sx={{ color: "white !important" }}
            >
              {fullscreenState.isFullscreen ? (
                <FullscreenExitIcon />
              ) : (
                <FullscreenIcon />
              )}
            </IconButton>
          </Tooltip>

          {/* More Actions */}
          <Tooltip title="More Actions">
            <IconButton
              onClick={(e) => setMoreActionsAnchor(e.currentTarget)}
              sx={{ color: "white !important" }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={moreActionsAnchor}
            open={Boolean(moreActionsAnchor)}
            onClose={() => setMoreActionsAnchor(null)}
          >
            <MenuItem onClick={handleFirstPage}>Go to First Page</MenuItem>
            <MenuItem onClick={handleLastPage}>Go to Last Page</MenuItem>
            <Divider />
            <MenuItem onClick={handleRotateClockwise}>
              Rotate Clockwise
            </MenuItem>
            <MenuItem onClick={handleRotateCounterClockwise}>
              Rotate Counter-Clockwise
            </MenuItem>
            <Divider />
            <MenuItem onClick={toggleTheme}>
              Toggle Theme ({darkTheme ? "Dark" : "Light"})
            </MenuItem>
            <MenuItem onClick={handleDocProperties}>
              Document Properties
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </>
  );
};

const PdfViewer = ({
  url,
  name,
  setIsLoad,
  message,
  pdfHeight,
  actions,
  hideToolbar,
  afterScrollContent,
  isFirstDocument,
  quoteId,
  policyId,
  mtaId,
  documentId,
}) => {
  const fileUrl = url || "https://localhost:3000";
  const [isRendering, setIsRendering] = useState(true);
  const dispatch = useDispatch();

  const { loggedUserData } = useSelector((store) => store.auth);

  const onDownloadClick = () => {
    if (isFirstDocument) {
      const payload = {
        is_downloaded: true,
        document_name: name,
        viewed_by: loggedUserData?.user_data?.invoffuserid,
      };
      if (quoteId) {
        dispatch(
          viewQuoteDocument({
            documentId,
            payload,
            quoteId,
          })
        );
      }
      if (policyId) {
        dispatch(viewPolicyDocument({ policyId: documentId, payload }));
      }
      if (mtaId) {
        dispatch(
          viewPolicyDocument({
            policyId: documentId,
            payload: {
              ...payload,
              mta_id: mtaId,
            },
          })
        );
      }
    }
  };

  // Initialize the engine
  const { engine, isLoading } = usePdfiumEngine();

  // Register plugins
  const plugins = useMemo(
    () => [
      createPluginRegistration(LoaderPluginPackage, {
        loadingOptions: {
          type: "url",
          pdfFile: {
            id: name || "pdf-document",
            url: fileUrl,
          },
        },
      }),
      createPluginRegistration(ViewportPluginPackage),
      createPluginRegistration(ScrollPluginPackage),
      createPluginRegistration(RenderPluginPackage),
      createPluginRegistration(InteractionManagerPluginPackage),
      createPluginRegistration(ZoomPluginPackage, {
        defaultZoomLevel: ZoomMode.FitWidth,
      }),
      createPluginRegistration(TilingPluginPackage, {
        tileSize: 768,
        overlapPx: 2.5,
      }),
      createPluginRegistration(ThumbnailPluginPackage, {
        width: 200,
        paddingY: 10,
      }),
      createPluginRegistration(PrintPluginPackage),
      createPluginRegistration(ExportPluginPackage, {
        defaultFileName: name || "document.pdf",
      }),
      createPluginRegistration(RotatePluginPackage),
      createPluginRegistration(SpreadPluginPackage),
      createPluginRegistration(FullscreenPluginPackage),
      createPluginRegistration(PanPluginPackage),
      createPluginRegistration(SearchPluginPackage),
      createPluginRegistration(SelectionPluginPackage),
    ],
    [fileUrl, name]
  );

  // Handle document load
  useEffect(() => {
    if (engine && !isLoading) {
      // Add a slight delay to ensure rendering has started
      const timer = setTimeout(() => {
        setIsRendering(false);
        setIsLoad?.(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [engine, isLoading, setIsLoad]);

  const renderError = () => {
    return (
      <Typography textAlign="center" p={3} color="white">
        {message ?? "Oops some error occurred, please try again"}
      </Typography>
    );
  };

  // Handle loading
  if (isLoading || !engine || isRendering) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          alignItems: "center",
          height: pdfHeight || "76vh",
          backgroundColor: "#525659",
        }}
      >
        <CircularProgress size={60} sx={{ color: "white !important" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: pdfHeight || "75vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
      }}
    >
      <EmbedPDF engine={engine} plugins={plugins} onError={renderError}>
        {!hideToolbar && <PdfToolbar onDownloadClick={onDownloadClick} />}
        <Viewport
          style={{
            backgroundColor: "#525659",
            flex: 1,
            overflow: "auto",
            width: "100%",
          }}
          className="pdf-viewport-scrollable"
        >
          <Scroller
            renderPage={({ width, height, pageIndex, scale }) => (
              <div style={{ width, height, marginBottom: "10px" }}>
                <RenderLayer pageIndex={pageIndex} scale={scale} />
              </div>
            )}
          />
          {afterScrollContent && <>{afterScrollContent}</>}
        </Viewport>
      </EmbedPDF>
      {actions && <>{actions}</>}
    </Box>
  );
};

export default PdfViewer;
